import { useEffect } from 'react';

import { Flex, FloraButton } from '@grupoboticario/flora-react';
import { PhoneIcon, SocialWhatsappIcon } from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';

import { ResponsiveFloraTypography, Skeleton } from 'components';
import { getFormattedPhone, isMobile as isMobileHelper, getWhatsappLink } from 'helpers';
import { useResellerSupervisor } from 'hooks';
import { newRelicMonitoringService } from 'services';

import { Info } from './components';
import Styles from './Supervisor.styles';

import type { SupervisorType } from 'domains/user';

export const Supervisor = () => {
  const { data, isLoading, isSuccess, isError } = useResellerSupervisor();
  const { supervisor } = isSuccess
    ? (data as SupervisorType)
    : ({ supervisor: { contacts: [], name: '' } } as SupervisorType);
  const isMobile = isMobileHelper();
  const supervisorHasContact = supervisor?.contacts.length;
  const supervisorPhone =
    supervisorHasContact && supervisor?.contacts.find((contact) => contact.value)?.value;
  const whatsappLink = supervisorHasContact ? getWhatsappLink(supervisorPhone || '') : '';

  useEffect(() => {
    if (isError) {
      newRelicMonitoringService.addPageAction('pageview_supervisor', {
        cd_interaction_detail: 'erro',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!supervisorHasContact || isError) return null;

  return (
    <Flex gap='$4' direction='column'>
      <ResponsiveFloraTypography fontSize='title' as='h2' fontWeight='bold'>
        Supervisor(a)
      </ResponsiveFloraTypography>

      <Styles.Wrapper direction='column' gap='$6'>
        <Info />

        <Flex direction='column' gap='$4'>
          {isLoading ? (
            <Skeleton height='$6' width='25%' />
          ) : (
            <Styles.Name size='bodyLargeShort' fontWeight='bold'>
              {supervisor?.name?.toLowerCase()}
            </Styles.Name>
          )}
          <Flex gap='$4' direction={isMobile ? 'column' : 'row'}>
            <Flex direction='column' gap='$2'>
              <Flex gap='$1'>
                <SocialWhatsappIcon color='$nonInteractiveAuxiliar' size={16} />
                <ResponsiveFloraTypography
                  color='$nonInteractiveAuxiliar'
                  fontSize='bodySmallShort'
                >
                  Telefone e WhatsApp
                </ResponsiveFloraTypography>
              </Flex>
              {Boolean(supervisorHasContact && !isLoading) ? (
                <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                  {getFormattedPhone(supervisorPhone || '')}
                </ResponsiveFloraTypography>
              ) : (
                <Skeleton height='$5' width='100%' />
              )}
            </Flex>

            <Flex gap='$4'>
              {Boolean(isMobile && supervisorHasContact) && (
                <Styles.ButtonContactWrapper>
                  <FloraButton
                    as={Link}
                    autotagging-flora='on'
                    color='$conversionButtonDefault'
                    data-flora-text='sup:ligar'
                    data-flora='button'
                    to={`tel:${supervisorPhone}`}
                    hierarchy='secondary'
                  >
                    <Flex gap='$2'>
                      <PhoneIcon size={16} />
                      Ligar
                    </Flex>
                  </FloraButton>
                </Styles.ButtonContactWrapper>
              )}
              {Boolean(supervisorHasContact) && (
                <Styles.ButtonContactWrapper>
                  <FloraButton
                    as={Link}
                    autotagging-flora='on'
                    color='$conversionButtonDefault'
                    data-flora-text={isMobile ? 'sup:conversar' : 'sup:conversar-no-whatsapp'}
                    data-flora='button'
                    rel='noopenner noreferrer'
                    target='_blank'
                    to={whatsappLink}
                  >
                    <Flex gap='$2'>
                      <SocialWhatsappIcon size={16} />
                      {isMobile ? 'Conversar' : 'Conversar no WhatsApp'}
                    </Flex>
                  </FloraButton>
                </Styles.ButtonContactWrapper>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Styles.Wrapper>
    </Flex>
  );
};
